import type { DashboardOrder, GroupDashboardOrder, OrderDetails, SingleDashboardOrder } from '@/entities/order/model'
import {
  getDriverName,
} from '@/entities/order/lib'
import {
  mockArrivedOrder,
  mockArrivedOrders,
  mockDeliveringOrder,
  mockDeliveringOrders,
  mockGroupArrivedOrders,
  mockGroupDeliveringOrders,
  mockGroupPartnerOnTheWayOrders,
  mockGroupReadyToCollectOrders,
  mockOnTheWayOrders,
  mockOnTheWayOrderWithETA,
  mockReadyToCollectOrder,
  mockReadyToCollectOrders,
  mockReadyToPickOrder,
  mockReadyToPickOrders,
} from './orders.mocks'

/**
 * Helper to create a SingleDashboardOrder from an OrderDetails object
 */
export function createSingleOrder(order: OrderDetails): SingleDashboardOrder {
  return {
    type: 'single',
    id: order.orderId,
    data: order,
  }
}

/**
 * Helper to create a GroupDashboardOrder from an OrderGroupDetails object
 */
export function createGroupOrder(orders: OrderDetails[]): GroupDashboardOrder {
  return {
    type: 'group',
    id: orders[0].orderId,
    data: {
      id: orders[0].orderId,
      status: orders[0].status,
      assignee: getDriverName(orders[0].hsFields) || '-',
      orders,
    },
  }
}

export const mockSingleArrivedOrders: SingleDashboardOrder[] = Array.from({ length: 3 }, (_, i) => createSingleOrder(mockArrivedOrders[i]))

// Create single dashboard orders from the existing mocks
export const mockSingleDashboardOrders: SingleDashboardOrder[] = [
  createSingleOrder(mockDeliveringOrder),
  createSingleOrder(mockReadyToCollectOrder),
  createSingleOrder(mockReadyToPickOrder),
  createSingleOrder(mockArrivedOrder),
]

// Create a group dashboard order
export const mockGroupDashboardOrder: GroupDashboardOrder = createGroupOrder(mockOnTheWayOrders)

// Combined array of all dashboard orders
export const mockAllDashboardOrders: DashboardOrder[] = [
  ...mockSingleDashboardOrders,
  mockGroupDashboardOrder,
]

// Group orders by status for testing the dashboard
export const mockGroupedDashboardOrders: Record<string, DashboardOrder[]> = {
  'On the way': [
    mockGroupDashboardOrder,
  ],
  'Delivering': [
    mockSingleDashboardOrders[0],
  ],
  'Ready to collect': [
    mockSingleDashboardOrders[1],
  ],
  'Ready to pick': [
    mockSingleDashboardOrders[2],
  ],
  'Arrived': [
    mockSingleDashboardOrders[3],
  ],
}

// Mock data for testing search functionality
export const mockSearchableDashboardOrders: DashboardOrder[] = [
  createSingleOrder(mockDeliveringOrder),
  createSingleOrder(mockReadyToCollectOrder),
  createSingleOrder(mockReadyToPickOrder),
  createSingleOrder(mockArrivedOrder),
  createGroupOrder(mockOnTheWayOrders),
]

export const mockComprehensiveDashboardOrders: DashboardOrder[] = [
  createSingleOrder(mockArrivedOrder), // Error state
  createSingleOrder(mockOnTheWayOrderWithETA), // Has ETA
  createSingleOrder(mockReadyToPickOrder), // Normal, no ETA
  createSingleOrder(mockArrivedOrder), // Error state
  createGroupOrder(mockGroupPartnerOnTheWayOrders), // Group order
]

export const mockOrdersBoard: Record<string, DashboardOrder[]> = {
  OnTheWay: mockOnTheWayOrders.map(order => createSingleOrder(order)),
  Arrived: mockArrivedOrders.map(order => createSingleOrder(order)),
  Delivering: mockDeliveringOrders.map(order => createSingleOrder(order)),
  ReadyToCollect: mockReadyToCollectOrders.map(order => createSingleOrder(order)),
  ReadyToPick: mockReadyToPickOrders.map(order => createSingleOrder(order)),
}

export const mockOrdersBoardWithGroupOrders: Record<string, DashboardOrder[]> = {
  OnTheWay: [
    ...mockOnTheWayOrders.map(order => createSingleOrder(order)),
    createGroupOrder(mockGroupPartnerOnTheWayOrders),
  ],
  Arrived: [
    ...mockArrivedOrders.map(order => createSingleOrder(order)),
    createGroupOrder(mockGroupArrivedOrders),
  ],
  DeliveringToCustomer: [
    ...mockDeliveringOrders.map(order => createSingleOrder(order)),
    createGroupOrder(mockGroupDeliveringOrders),
  ],
  ReadyToCollect: [
    ...mockReadyToCollectOrders.map(order => createSingleOrder(order)),
    createGroupOrder(mockGroupReadyToCollectOrders),
  ],
  ReadyToPick: mockReadyToPickOrders.map(order => createSingleOrder(order)),
}

// Tutorial-specific mock data with isAcknowledged: true to prevent pulsing animations
export const mockTutorialSafeArrivedOrders: OrderDetails[] = mockArrivedOrders.map(order => ({
  ...order,
  isAcknowledged: true, // Prevents pulsing animation during tutorial
}))

export const mockTutorialSafeDeliveringOrders: OrderDetails[] = mockDeliveringOrders.map(order => ({
  ...order,
  isAcknowledged: true, // Prevents pulsing animation during tutorial
}))

export const mockTutorialSafeOnTheWayOrders: OrderDetails[] = mockOnTheWayOrders.map(order => ({
  ...order,
  isAcknowledged: true, // Prevents pulsing animation during tutorial
}))

// Tutorial-specific orders board with no pulsing animations
export const mockTutorialSafeOrdersBoard: Record<string, DashboardOrder[]> = {
  OnTheWay: [
    ...mockTutorialSafeOnTheWayOrders.map(order => createSingleOrder(order)),
    createGroupOrder(mockGroupPartnerOnTheWayOrders.map(order => ({
      ...order,
      isAcknowledged: true,
    }))),
  ],
  Arrived: [
    ...mockTutorialSafeArrivedOrders.map(order => createSingleOrder(order)),
    createGroupOrder(mockGroupArrivedOrders.map(order => ({
      ...order,
      isAcknowledged: true,
    }))),
  ],
  DeliveringToCustomer: [
    ...mockTutorialSafeDeliveringOrders.map(order => createSingleOrder(order)),
    createGroupOrder(mockGroupDeliveringOrders.map(order => ({
      ...order,
      isAcknowledged: true,
    }))),
  ],
  ReadyToCollect: mockReadyToCollectOrders.map(order => createSingleOrder({
    ...order,
    isAcknowledged: true,
  })),
  ReadyToPick: mockReadyToPickOrders.map(order => createSingleOrder({
    ...order,
    isAcknowledged: true,
  })),
}
